// Image Flags Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class ImageFlags extends HTMLElement {
  constructor() {
    super();
    this.flags = [];
    this.isLoading = false;
    this.currentUser = null;
    this.statusFilter = 'pending'; // pending, approved, rejected, or 'all'
    this.isLoadingFlags = false;
    this.actionHandler = null; // Store handler reference for cleanup
  }

  connectedCallback() {
    this.render();
    this.setupAuthListener();
  }

  disconnectedCallback() {
    if (this.handleAuthStatusLoaded) {
      document.removeEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
    }
    // Remove action handler
    const content = this.querySelector('.flags-content');
    if (content && this.actionHandler) {
      content.removeEventListener('click', this.actionHandler);
    }
  }

  setupAuthListener() {
    this.handleAuthStatusLoaded = (e) => {
      const { user, authenticated } = e.detail;

      if (authenticated && user && user.role === 'admin') {
        this.currentUser = user;
        if (this.isVisible()) {
          this.loadFlags();
        }
      } else {
        this.currentUser = null;
      }
    };

    document.addEventListener('auth-status-loaded', this.handleAuthStatusLoaded);
  }

  isVisible() {
    return this.offsetParent !== null && !this.style.display.includes('none');
  }

  render() {
    this.innerHTML = `
      <div class="moderation-flags">
        <div class="flags-header">
          ${renderPageTitle('🖼️', 'Image Flags')}
          <p>Review and moderate flagged images</p>
        </div>

        <div class="flags-filters">
          <button class="filter-btn ${this.statusFilter === 'all' ? 'active' : ''}" data-filter="all">
            All
          </button>
          <button class="filter-btn ${this.statusFilter === 'pending' ? 'active' : ''}" data-filter="pending">
            Pending
          </button>
          <button class="filter-btn ${this.statusFilter === 'approved' ? 'active' : ''}" data-filter="approved">
            Approved
          </button>
          <button class="filter-btn ${this.statusFilter === 'rejected' ? 'active' : ''}" data-filter="rejected">
            Rejected
          </button>
        </div>

        <div class="flags-content">
          ${this.renderContent()}
        </div>
      </div>
    `;

    // Attach filter button listeners
    this.querySelectorAll('.filter-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const filter = e.target.dataset.filter;
        this.statusFilter = filter;
        this.updateFilters();
        this.loadFlags();
      });
    });
  }

  updateFilters() {
    this.querySelectorAll('.filter-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.filter === this.statusFilter);
    });
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading image flags...</div>
        </div>
      `;
    }

    if (this.flags.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">🖼️</div>
          <h3>No flags found</h3>
          <p>There are no image flags with the selected status.</p>
        </div>
      `;
    }

    return `
      <div class="flags-list">
        ${this.flags.map(flag => this.renderFlagItem(flag)).join('')}
      </div>
    `;
  }

  renderFlagItem(flag) {
    const statusClass = flag.status || 'pending';
    const canReview = statusClass === 'pending';
    const imageUrl = flag.image_url || '';
    const isBlocked = imageUrl.includes('blocked') || imageUrl.length === 0;

    return `
      <div class="flag-item">
        <div class="flag-header">
          <div class="flag-info">
            <span class="flag-status ${statusClass}">${statusClass}</span>
          </div>
        </div>

        <div class="flag-image-container">
          ${isBlocked ? `
            <div class="flag-image-blocked">[Image blocked or unavailable]</div>
          ` : `
            <img src="${escapeHtml(imageUrl)}" 
                 alt="Flagged image" 
                 class="flag-image"
                 loading="lazy"
                 onerror="this.parentElement.innerHTML='<div class=\\'flag-image-blocked\\'>[Image failed to load]</div>'"
                 referrerpolicy="no-referrer"
                 crossorigin="anonymous">
          `}
        </div>

        <div class="flag-details">
          <div class="flag-detail">
            <span class="flag-detail-label">Image URL</span>
            <span class="flag-detail-value" style="word-break: break-all; font-size: 0.875rem;">
              ${escapeHtml(imageUrl.substring(0, 100))}${imageUrl.length > 100 ? '...' : ''}
            </span>
          </div>
          <div class="flag-detail">
            <span class="flag-detail-label">Reason</span>
            <span class="flag-detail-value">${escapeHtml(flag.reason || 'N/A')}</span>
          </div>
          <div class="flag-detail">
            <span class="flag-detail-label">Flagged By</span>
            <span class="flag-detail-value">${escapeHtml(flag.flagged_by_username || 'Unknown')}</span>
          </div>
          <div class="flag-detail">
            <span class="flag-detail-label">Flagged At</span>
            <span class="flag-detail-value">${this.formatDate(flag.created_at)}</span>
          </div>
          ${flag.reviewed_at ? `
            <div class="flag-detail">
              <span class="flag-detail-label">Reviewed At</span>
              <span class="flag-detail-value">${this.formatDate(flag.reviewed_at)}</span>
            </div>
          ` : ''}
          ${flag.post_id ? `
            <div class="flag-detail">
              <span class="flag-detail-label">Post ID</span>
              <span class="flag-detail-value">#${flag.post_id}</span>
            </div>
          ` : ''}
          ${flag.comment_id ? `
            <div class="flag-detail">
              <span class="flag-detail-label">Comment ID</span>
              <span class="flag-detail-value">#${flag.comment_id}</span>
            </div>
          ` : ''}
        </div>

        ${canReview ? `
          <div class="flag-actions">
            <button class="flag-action-btn approve" 
                    data-flag-id="${flag.id}" 
                    data-action="approve">
              ✓ Approve
            </button>
            <button class="flag-action-btn reject" 
                    data-flag-id="${flag.id}" 
                    data-action="reject">
              ✗ Reject
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }

  async loadFlags() {
    if (this.isLoadingFlags) {
      return;
    }

    if (!this.currentUser || this.currentUser.role !== 'admin') {
      return;
    }

    if (!this.isVisible()) {
      return;
    }

    this.isLoadingFlags = true;
    this.isLoading = true;
    this.updateContent();

    try {
      const url = this.statusFilter === 'all'
        ? '/admin/image-flags'
        : `/admin/image-flags?status=${this.statusFilter}`;

      const response = await window.API.request(url);

      if (response && response.flags) {
        this.flags = response.flags;
      } else {
        this.flags = [];
      }
    } catch (error) {
      window.logger?.error('Failed to load image flags:', error);
      this.flags = [];
      const content = this.querySelector('.flags-content');
      if (content) {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'error-state';
        errorDiv.textContent = 'Failed to load image flags. Please try again.';
        content.innerHTML = '';
        content.appendChild(errorDiv);
        return;
      }
    } finally {
      this.isLoading = false;
      this.isLoadingFlags = false;
      this.updateContent();
      // attachActionListeners is called in updateContent, no need to call it again
    }
  }

  attachActionListeners() {
    const content = this.querySelector('.flags-content');
    if (!content) return;

    // Remove existing handler if any
    if (this.actionHandler) {
      content.removeEventListener('click', this.actionHandler);
    }

    // Use event delegation - attach one listener to the container
    this.actionHandler = async (e) => {
      const btn = e.target.closest('.flag-action-btn');
      if (!btn) return;

      const flagId = btn.dataset.flagId;
      const action = btn.dataset.action;

      if (!flagId || !action) return;

      btn.disabled = true;

      try {
        await window.API.request(`/admin/image-flags/${flagId}/review`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: action === 'approve' ? 'approved' : 'rejected'
          })
        });

        // Reload flags after review
        await this.loadFlags();
      } catch (error) {
        window.logger?.error('Failed to review flag:', error);
        btn.disabled = false;
        alert('Failed to review flag. Please try again.');
      }
    };

    content.addEventListener('click', this.actionHandler);
  }

  updateContent() {
    const content = this.querySelector('.flags-content');
    if (content) {
      content.innerHTML = this.renderContent();
      this.attachActionListeners();
    }
  }


  formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (_e) {
      return dateString;
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
    if (this.isVisible() && user && user.role === 'admin') {
      this.loadFlags();
    }
  }
}

// Register the custom element
customElements.define('image-flags', ImageFlags);

